import { Pressable, View, Image } from "react-native";
import { IAgentStatus } from "../types/agent-status";
import { formatPhoneNumber } from "react-phone-number-input";
import ActivateLomanButton from "./feature/home/toggle-loman/activate-loman-button";
import StoreStatus from "./feature/home/store-status";
import { useFormattedDate } from "../hooks/useFormattedDate";
const pauseIcon = require("../assets/pause_icon.png");

const AgentStatus = ({
  status,
  fallbackNumber,
  compact = false,
  loading = false,
  handleActivate,
  handleDeactivate,
}: {
  status: IAgentStatus | null;
  fallbackNumber?: string | null;
  compact?: boolean;
  loading?: boolean;
  handleActivate: () => void;
  handleDeactivate: () => void;
}) => {
  const { formatTime, formatDateShort } = useFormattedDate();

  function getPauseEndTimeMessage(agentStatus: IAgentStatus) {
    if (agentStatus.mode === "paused" && agentStatus.pausedUntil) {
      const now = new Date();
      if (agentStatus.pausedUntil.getTime() > now.getTime()) {
        const pausedUntilDate = formatDateShort(
          agentStatus.pausedUntil.toISOString()
        );
        const nowDate = formatDateShort(now.toISOString());
        const isTomorrow = pausedUntilDate !== nowDate; // pause modal is at most 12 hours, so if the date is different, it's tomorrow

        const timeFormat = formatTime(agentStatus.pausedUntil.toISOString());

        const pauseEndTimeString = timeFormat + (isTomorrow ? " tomorrow" : "");

        return `${pauseEndTimeString}`;
      }
    }
    return null;
  }

  if (!status) {
    return null;
  }

  if (loading) {
    return (
      <div className="border p-3 rounded-lg bg-gray-100 border-gray-200 h-[58px] flex items-center">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2.5">
            <div className="w-2.5 h-2.5 bg-gray-500 rounded-full"></div>
            <span className="text-sm font-semibold text-gray-800">
              Loading...
            </span>
          </div>
        </div>
      </div>
    );
  }

  if (status.panic === true) {
    if (compact) {
      return (
        <div className="h-[54px] border-red-200 bg-red-50 border p-2 rounded-lg justify-center items-center flex">
          <div className="h-3 w-3 bg-red-500 rounded-full"></div>
        </div>
      );
    } else {
      return (
        <div className="border p-3 rounded-lg bg-gradient-to-r from-red-50 to-red-50 border-red-200 flex-1">
          <div className="flex items-center justify-between mb-1">
            <div className="flex items-center gap-2.5">
              <div className="w-2.5 h-2.5 bg-red-500 rounded-full"></div>
              <span className="text-sm font-semibold text-red-800">
                Service Interruption
              </span>
            </div>
          </div>
          <div className="text-sm font-normal text-gray-600 ml-5">
            Loman is temporarily out of service.
          </div>
        </div>
      );
    }
  }

  if (status.mode === "active") {
    if (compact) {
      return (
        <div className="h-[54px] border-emerald-200 bg-emerald-50 border p-2 rounded-lg justify-center items-center flex animate-pulse">
          <div className="h-3 w-3 bg-emerald-500 rounded-full"></div>
        </div>
      );
    } else {
      return (
        <View className="flex flex-col gap-2">
          <StoreStatus />
          <Pressable
            className="border p-3 rounded-lg bg-gradient-to-r from-emerald-50 to-teal-50 border-emerald-200 flex-1"
            onPress={handleDeactivate}
          >
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2.5">
                <div className="w-2.5 h-2.5 bg-emerald-500 rounded-full animate-pulse"></div>
                <span className="text-sm font-semibold text-emerald-700">
                  Active - Handling calls
                </span>
              </div>
              <Image
                source={pauseIcon}
                className="w-4 h-4"
                style={{ width: 16, height: 16 }}
                resizeMode="contain"
              />
            </div>
          </Pressable>
        </View>
      );
    }
  }

  if (status.mode === "paused") {
    if (compact) {
      return (
        <div className="h-[54px] border-amber-200 bg-amber-50 border p-2 rounded-lg justify-center items-center flex">
          <div className="h-3 w-3 bg-amber-500 rounded-full"></div>
        </div>
      );
    } else {
      return (
        <View className="flex flex-col">
          <div className="border p-3 rounded-lg bg-gradient-to-r from-yellow-50 to-amber-50 border-amber-200 gap-1">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2.5">
                <div className="w-2.5 h-2.5 bg-amber-500 rounded-full"></div>
                <span className="text-sm font-semibold text-amber-800">
                  Phone System Offline
                </span>
              </div>
            </div>
            <p className="text-sm font-normal text-gray-600 ml-5 mb-2">
              {`until ${getPauseEndTimeMessage(status)}`}
            </p>
            {fallbackNumber ? (
              <div className="text-xs text-gray-600 ml-5">
                <div className="text-xs text-gray-600 mb-0.5">
                  Calls are being forwarded to:
                </div>
                <div className="text-xs text-gray-600 underline underline-offset-2 font-semibold">
                  {formatPhoneNumber(fallbackNumber)}
                </div>
              </div>
            ) : (
              <div className="text-xs text-gray-500 ml-5 leading-relaxed">
                <span className="font-semibold">Warning:</span> Customer calls
                are being dropped. Re-activate Loman or contact support to add a
                backup number.
              </div>
            )}
          </div>
          <View className="mt-2">
            <ActivateLomanButton onClick={handleActivate} />
          </View>
        </View>
      );
    }
  }

  if (status.mode === "disabled") {
    if (compact) {
      return (
        <div className="h-[54px] border-red-200 bg-red-50 border p-2 rounded-lg justify-center items-center flex">
          <div className="h-3 w-3 bg-red-500 rounded-full"></div>
        </div>
      );
    } else {
      return (
        <View className="flex flex-col">
          <div className="border p-3 rounded-lg bg-gradient-to-r from-red-50 to-red-50 border-red-200 gap-1">
            <div className="flex items-center justify-between mb-1.5">
              <div className="flex items-center gap-2.5">
                <div className="w-2.5 h-2.5 bg-red-500 rounded-full"></div>
                <span className="text-sm font-semibold text-red-800">
                  Phone System Disabled
                </span>
              </div>
            </div>
            {fallbackNumber ? (
              <div className="text-xs text-gray-600 ml-5">
                <div className="text-xs text-gray-600 mb-0.5">
                  Calls are being forwarded to:
                </div>
                <div className="text-xs text-gray-600 underline underline-offset-2 font-semibold">
                  {formatPhoneNumber(fallbackNumber)}
                </div>
              </div>
            ) : (
              <div className="text-xs text-gray-500 ml-5 leading-relaxed">
                <span className="font-semibold">Warning:</span> Customer calls
                are being dropped. Re-activate Loman or contact support to add a
                backup number.
              </div>
            )}
          </div>
          <View className="mt-2">
            <ActivateLomanButton onClick={handleActivate} />
          </View>
        </View>
      );
    }
  }

  return <></>;
};

export default AgentStatus;
